import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  addLifecycleStage,
  deleteLifecycleStage,
  fetchLifecycleStages,
  fetchLifecycleStageSystemUsage,
  renameLifecycleStage,
  reorderLifecycleStage,
  setDefaultLifecycleStage,
} from '../routines';
import envConfig from '../../config';
import { request } from '../../common/util/RestClient';
import constants, {
  LEVEL_ID_MAP,
} from '../../common/constants/CommonConstants';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import { fetchMethodMapping } from '../../common/constants/EntityActions';

function* fetchLifecycleStagesSaga({ payload: entityLevel }) {
  try {
    const url = `${envConfig.APIG_URL}core/lifecycles?level=${entityLevel}`;
    const response = yield call(request, url);
    yield put(fetchLifecycleStages.success({ entityLevel, response }));
  } catch (error) {
    yield put(fetchLifecycleStages.failure({ error }));
  }
}

function* fetchLifecycleStagesSystemUsageSaga({ payload }) {
  const { lifecycleStageId } = payload;

  try {
    const response = yield call(
      request,
      `${envConfig.APIG_URL}core/lifecycles/${lifecycleStageId}/systemUsage`
    );

    const { data: usage } = response;

    yield put(fetchLifecycleStageSystemUsage.success({ usage }));
  } catch (error) {
    yield put(fetchLifecycleStageSystemUsage.failure({ error }));
  }
}

function* reorderLifecycleStageSaga({ payload }) {
  const { entityLevel, lifecycleStageId, newIndex: reorderTo } = payload;
  const entityLevelId = LEVEL_ID_MAP[entityLevel];

  try {
    const url = `${envConfig.APIG_URL}core/lifecycles/${lifecycleStageId}/reorder`;
    const config = {
      method: 'POST',
      payload: { entityLevel: entityLevelId, reorderTo },
    };
    yield call(request, url, config);
    yield call(fetchLifecycleStagesSaga, { payload: entityLevel });
    yield put(reorderLifecycleStage.success(payload));
  } catch (error) {
    yield put(reorderLifecycleStage.failure({ error }));
  }
}

function* setDefaultLifecycleStageSaga({ payload }) {
  const { entityLevel, lifecycleStageId } = payload;
  const entityLevelId = LEVEL_ID_MAP[entityLevel];

  try {
    const url = `${envConfig.APIG_URL}core/lifecycles/default`;
    const config = {
      method: 'POST',
      payload: { entityLevel: entityLevelId, lifecycleStageId },
    };
    const response = yield call(request, url, config);
    yield call(fetchLifecycleStagesSaga, { payload: entityLevel });
    yield put(
      setDefaultLifecycleStage.success({
        response,
        successMessage: SUCCESS_MESSAGES.CHANGE_DEFAULT_STAGE,
      })
    );
  } catch (error) {
    yield put(setDefaultLifecycleStage.failure({ error }));
  }
}

function* addLifecycleStageSaga({ payload }) {
  const { entityLevel } = payload;
  const entityLevelId = LEVEL_ID_MAP[entityLevel];

  try {
    const url = `${envConfig.APIG_URL}core/lifecycles`;
    const config = {
      method: 'POST',
      payload: { ...payload, entityLevel: entityLevelId },
    };
    const response = yield call(request, url, config);
    yield call(fetchLifecycleStagesSaga, { payload: entityLevel });
    yield put(
      addLifecycleStage.success({
        response,
        successMessage: SUCCESS_MESSAGES.ADD_STAGE,
      })
    );
  } catch (error) {
    yield put(addLifecycleStage.failure({ error }));
  }
}

function* deleteLifecycleStageSaga({ payload }) {
  const { entityLevel, lifecycleStageId, moveTo } = payload;
  const entityLevelId = LEVEL_ID_MAP[entityLevel];
  try {
    const url = `${envConfig.APIG_URL}core/lifecycles/${lifecycleStageId}`;
    const config = {
      method: 'DELETE',
      payload: { entityLevel: entityLevelId, moveTo },
    };
    const response = yield call(request, url, config);
    yield call(fetchLifecycleStagesSaga, { payload: entityLevel });
    yield put(
      deleteLifecycleStage.success({
        response,
        successMessage: SUCCESS_MESSAGES.DELETE_STAGE,
      })
    );
    const processedEntityLevel =
      entityLevel === constants.OPPORTUNITY_LEVEL || constants.RISK_LEVEL
        ? constants.PIPELINE_LEVEL
        : entityLevel;

    yield put(
      fetchMethodMapping[processedEntityLevel].trigger({ usePrevPayload: true })
    );
  } catch (error) {
    yield put(deleteLifecycleStage.failure({ error }));
  }
}

function* renameLifecycleStageSaga({ payload }) {
  const {
    entityLevel,
    lifecycleStageId,
    displayName,
    description,
    isBoardVisible,
    colorCode,
  } = payload;
  const entityLevelId = LEVEL_ID_MAP[entityLevel];

  try {
    const url = `${envConfig.APIG_URL}core/lifecycles/${lifecycleStageId}/rename`;
    const config = {
      method: 'PATCH',
      payload: {
        entityLevel: entityLevelId,
        displayName,
        description,
        isBoardVisible,
        colorCode,
      },
    };
    const response = yield call(request, url, config);
    yield call(fetchLifecycleStagesSaga, { payload: entityLevel });
    yield put(renameLifecycleStage.success(response));
  } catch (error) {
    yield put(renameLifecycleStage.failure({ error }));
  }
}

export default function* root() {
  yield all([
    takeEvery(fetchLifecycleStages.TRIGGER, fetchLifecycleStagesSaga),
    takeLatest(
      fetchLifecycleStageSystemUsage.TRIGGER,
      fetchLifecycleStagesSystemUsageSaga
    ),
    takeLatest(reorderLifecycleStage.TRIGGER, reorderLifecycleStageSaga),
    takeLatest(setDefaultLifecycleStage.TRIGGER, setDefaultLifecycleStageSaga),
    takeLatest(addLifecycleStage.TRIGGER, addLifecycleStageSaga),
    takeLatest(deleteLifecycleStage.TRIGGER, deleteLifecycleStageSaga),
    takeLatest(renameLifecycleStage.TRIGGER, renameLifecycleStageSaga),
  ]);
}
